import { useEffect, useState } from "react";
import { Loader2, Ruler, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

export const PageTarget = ({ p, theme, ensureSaved, onStarted, onPatch }) => {
  const [target, setTarget] = useState(p.target_pages || "");
  const [busy, setBusy] = useState(false);
  const [trim, setTrim] = useState(false);
  useEffect(() => setTarget(p.target_pages || ""), [p.target_pages]);
  const job = p.fit_job || {};
  const running = job.status === "running";
  const pages = p.files?.page_count;

  const fitNow = async () => {
    const t = parseInt(target, 10);
    if (!t) return toast.error("Enter a target number of pages");
    const low = 3 + (p.sections?.length || 0);
    if (t < low) return toast.error(`With ${low - 3} chapters the shortest possible PDF is about ${low} pages (each chapter starts a new page, plus cover, contents and back cover)`);
    if (pages && t < pages && !trim) return toast.error(`The PDF is ${pages} pages. Tick “Allow trimming” to shorten it — otherwise the AI only ever adds detail.`);
    if (!window.confirm(`Let the AI ${pages && t < pages ? "trim" : "expand"} the chapters so the PDF lands near ${t} pages? You can undo it afterwards.`)) return;
    setBusy(true);
    try {
      await ensureSaved();
      const r = await api.post(`/products/${p.id}/fit-pages`, { target: t, allow_trim: trim });
      onPatch({ fit_job: r.data, target_pages: t });
      onStarted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    if (!window.confirm("Put the chapters back the way they were before fitting?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/products/${p.id}/fit-pages/undo`);
      onPatch({ sections: r.data.sections, files: r.data.files, fit_job: null, sections_before_fit: null });
      toast.success("Chapters restored and files rebuilt");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="page-target">
      <Ruler size={15} style={{ color: theme.dark }} />
      <span className="text-[#5C5255]">Target</span>
      <Input type="number" min={4} max={400} value={target} onChange={(e) => setTarget(e.target.value)} disabled={running} className="w-20 h-8 bg-white rounded-none" data-testid="page-target-input" />
      <span className="text-[#5C5255]">pages</span>
      <Button size="sm" onClick={fitNow} disabled={running || busy} className="rounded-none gap-1.5 h-8" style={{ background: theme.dark }} data-testid="page-target-fit-btn">
        {running && <Loader2 size={13} className="animate-spin" />}{running ? "Fitting…" : "Fit to target"}
      </Button>
      <label className="flex items-center gap-1.5 text-xs text-[#5C5255]" title="Off: the AI only ever adds detail. On: it may shorten text when your target is lower.">
        <input type="checkbox" checked={trim} onChange={(e) => setTrim(e.target.checked)} disabled={running} data-testid="page-target-allow-trim" /> Allow trimming
      </label>
      {running && (
        <span className="text-xs text-[#5C5255]" data-testid="page-target-progress">
          {job.message}{job.total ? ` · ${job.done || 0}/${job.total}` : ""}{job.after ? ` · now ${job.after} pages` : ""}
        </span>
      )}
      {job.status === "done" && (
        <span className={`text-xs ${job.on_target ? "text-[#2D5A3F]" : "text-[#B7791F]"}`} data-testid="page-target-result">
          {job.before} → {job.after} pages{job.on_target ? " · on target" : ` · closest it could get to ${job.target}`}
        </span>
      )}
      {job.status === "failed" && <span className="text-xs text-[#9B2C2C]" data-testid="page-target-error">{job.error}</span>}
      {!running && p.sections_before_fit?.length > 0 && (
        <button onClick={undo} disabled={busy} className="text-xs flex items-center gap-1 hover:underline text-[#5C5255]" data-testid="page-target-undo-btn"><Undo2 size={12} /> Undo fit</button>
      )}
    </div>
  );
};
